// src/pages/Users/Inscription/InscriptionAdherent.jsx

import React, {
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  FaAddressCard,
  FaPlus,
  FaSearch,
} from "react-icons/fa";

import {
  ToastContainer,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";

import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import useScrollToTop from "../../../hooks/useScollToTop";

import { useUser } from "../../Home/UserContext";

import { getRoleFromRoleId } from "../../../utils/roleHelper";

import RenewalModal from "./RenewalModal";

import UsefulInformationPanel from "./dossier/components/UsefulInformationPanel";

import AdherentList from "./dossier/components/AdherentList";

import useInscriptionDossierPage from "./dossier/hooks/useInscriptionDossierPage";

import DossierActionButton from "./dossier/ui/DossierActionButton";

import { normalizeText } from "./dossier/utils/dossierInscription.utils";

/* -------------------------------------------------------------------------- */
/*                            InscriptionAdherent                             */
/* -------------------------------------------------------------------------- */

export default function InscriptionAdherent() {
  useScrollToTop();

  const navigate =
    useNavigate();

  const { user } = useUser();

  const userRole =
    getRoleFromRoleId(
      user?.role_id,
    );

  const userId =
    user?.userId;

  /* ---------------------------------------------------------------------- */
  /* Data                                                                   */
  /* ---------------------------------------------------------------------- */

  const {
    loading,

    adherents,
    dossiers,
    groupNames,

    segments,

    settings,
    activeSeason,

    refreshDossier,
    refreshAll,
  } =
    useInscriptionDossierPage(
      userId,
    );

  /* ---------------------------------------------------------------------- */
  /* UI                                                                     */
  /* ---------------------------------------------------------------------- */

  const [search, setSearch] =
    useState("");

  /*
   * Un seul dossier ouvert à la fois.
   *
   * C'est particulièrement important
   * sur smartphone.
   */
  const [
    expandedId,
    setExpandedId,
  ] = useState(null);

  const [
    renewAdherent,
    setRenewAdherent,
  ] = useState(null);

  /* ---------------------------------------------------------------------- */
  /* Recherche                                                              */
  /* ---------------------------------------------------------------------- */

  const filteredAdherents =
    useMemo(() => {
      const query =
        normalizeText(search);

      if (!query) {
        return adherents;
      }

      return adherents.filter(
        (adherent) => {
          const groups =
            groupNames[
              adherent.id
            ] || [];

          const content =
            normalizeText(
              [
                adherent.nom,
                adherent.prenom,
                adherent.statut_inscription,
                adherent.saison_active,

                ...groups,
              ].join(" "),
            );

          return content.includes(
            query,
          );
        },
      );
    }, [
      adherents,
      groupNames,
      search,
    ]);

  /* ---------------------------------------------------------------------- */
  /* Toggle dossier                                                         */
  /* ---------------------------------------------------------------------- */

  const handleToggle =
    (adherentId) => {
      setExpandedId(
        (current) =>
          current === adherentId
            ? null
            : adherentId,
      );
    };

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <section
      className="
        min-h-screen
        w-full
        max-w-full
        overflow-x-hidden

        bg-slate-50

        dark:bg-slate-950
      "
    >
      {/* Offset Navbar */}

      <div
        className="pt-[150px]"
        aria-hidden
      />

      <div
        className="
          mx-auto
          w-full
          max-w-7xl
          min-w-0

          px-3
          pb-12

          sm:px-6
          lg:pb-16
        "
      >
        {/* -------------------------------------------------------------- */}
        {/* HeaderTitle conservé                                           */}
        {/* -------------------------------------------------------------- */}

        <HeaderTitle
          role="Utilisateur"
          roleId="2"
          title="Dossier d’inscription"
          subtitle="Suivez les documents, validations et règlements liés aux inscriptions de vos adhérents."
          icon={FaAddressCard}
          align="left"
          className="mb-4"
        />

        {/* -------------------------------------------------------------- */}
        {/* Navigation                                                     */}
        {/* -------------------------------------------------------------- */}

        <div
          className="
            mb-5
            w-full
            min-w-0
            max-w-full
            overflow-hidden
          "
        >
          <DashboardNavigation
            role={userRole}
          />
        </div>

        {/* -------------------------------------------------------------- */}
        {/* INFORMATIONS UTILES                                            */}
        {/* volontairement AVANT les dossiers                             */}
        {/* -------------------------------------------------------------- */}

        <UsefulInformationPanel
          segments={segments}
        />

        {/* -------------------------------------------------------------- */}
        {/* Dossiers                                                       */}
        {/* -------------------------------------------------------------- */}

        <section className="mt-5">
          {/* Header */}

          <div
            className="
              rounded-[1.75rem]

              border
              border-slate-200
              border-l-4
              border-l-[#013E8E]

              bg-white

              p-4

              shadow-sm

              dark:border-slate-800
              dark:border-l-blue-500
              dark:bg-slate-900

              sm:p-5
            "
          >
            <div
              className="
                flex
                flex-col
                gap-4

                sm:flex-row
                sm:items-center
                sm:justify-between
              "
            >
              <div>
                <p
                  className="
                    text-[10px]
                    font-black
                    uppercase
                    tracking-[0.15em]

                    text-[#013E8E]

                    dark:text-blue-300
                  "
                >
                  Mes adhérents
                </p>

                <h2
                  className="
                    mt-1
                    text-lg
                    font-black

                    text-slate-950

                    dark:text-white

                    sm:text-xl
                  "
                >
                  Dossiers
                  d'inscription
                </h2>

                <p
                  className="
                    mt-1
                    text-xs
                    leading-5
                    text-slate-500

                    dark:text-slate-400

                    sm:text-sm
                  "
                >
                  Consultez
                  l'avancement du
                  dossier et complétez
                  les éléments
                  nécessaires.
                </p>
              </div>

              <DossierActionButton
                variant="primary"
                icon={FaPlus}
                onClick={() =>
                  navigate(
                    "/ajouter-inscription-adherent",
                  )
                }
                className="w-full sm:w-auto"
              >
                Nouvel adhérent
              </DossierActionButton>
            </div>

            {/* Recherche */}

            <div className="mt-5">
              <div
                className="
                  flex
                  flex-col
                  gap-3

                  sm:flex-row
                  sm:items-center
                "
              >
                <div
                  className="
                    relative
                    min-w-0
                    flex-1
                  "
                >
                  <FaSearch
                    className="
                      pointer-events-none
                      absolute

                      left-3.5
                      top-1/2

                      -translate-y-1/2

                      text-sm
                      text-slate-400
                    "
                  />

                  <input
                    type="search"
                    value={search}
                    onChange={(
                      event,
                    ) =>
                      setSearch(
                        event.target
                          .value,
                      )
                    }
                    placeholder="Rechercher un adhérent, groupe..."
                    className="
                      w-full

                      rounded-xl

                      border
                      border-slate-200

                      bg-slate-50

                      py-3
                      pl-10
                      pr-3

                      text-sm

                      text-slate-700

                      outline-none

                      transition

                      placeholder:text-slate-400

                      focus:border-blue-300
                      focus:bg-white
                      focus:ring-4
                      focus:ring-blue-100

                      dark:border-slate-700
                      dark:bg-slate-950
                      dark:text-slate-200
                      dark:focus:ring-blue-500/10
                    "
                  />
                </div>

                <div
                  className="
                    shrink-0

                    text-xs
                    font-bold

                    text-slate-400

                    sm:text-right
                  "
                >
                  {
                    filteredAdherents.length
                  }{" "}
                  /{" "}
                  {
                    adherents.length
                  }{" "}
                  adhérent
                  {adherents.length >
                  1
                    ? "s"
                    : ""}
                </div>
              </div>
            </div>
          </div>

          {/* Liste */}

          <div className="mt-3">
            <AdherentList
              loading={loading}
              adherents={
                filteredAdherents
              }
              dossiers={
                dossiers
              }
              groups={
                groupNames
              }
              user={user}
              settings={
                settings
              }
              activeSeason={
                activeSeason
              }
              expandedId={
                expandedId
              }
              onToggle={
                handleToggle
              }
              onRenew={
                setRenewAdherent
              }
              refreshDossier={
                refreshDossier
              }
              refreshAll={
                refreshAll
              }
            />
          </div>
        </section>
      </div>

      {/* -------------------------------------------------------------- */}
      {/* Renouvellement                                                 */}
      {/* -------------------------------------------------------------- */}

      <RenewalModal
        isOpen={Boolean(
          renewAdherent,
        )}
        adherent={
          renewAdherent
        }
        nextSeason={
          activeSeason
        }
        onClose={async (
          saved,
        ) => {
          setRenewAdherent(
            null,
          );

          if (saved) {
            await refreshAll();

            setExpandedId(
              null,
            );
          }
        }}
      />

      <ToastContainer />
    </section>
  );
}